import { GIT_BOOK_TP_SL_ORDERS } from '@ui/constants/links';
import { Link } from '@ui/lib/park/ui/link';
import { Text } from '@ui/lib/park/ui/text';
import { Nullable } from '@ui/types/utility';
import { getOpenTradeQty } from '@ui/utils/trade/getOpenTradeQty';
import { zeroInput } from '@ui/utils/validation/rules/zeroInput';
import BigNumber from 'bignumber.js';
import { isEmpty, isNil } from 'lodash-es';
import { TFunction } from 'next-i18next';
import { Box } from 'styled-system/jsx';

export type TriggerPriceError =
  | 'should_be_above_market_price'
  | 'should_have_non_zero_position_size'
  | 'should_be_below_market_price'
  | 'zero_input';

export const validateTriggerPrice = ({
  isLong,
  marketPrice,
  triggerPrice,
  positionSizeUsd,
  isLeverageReachMax,
}: {
  marketPrice: Nullable<number>;
  isLong: boolean;
  triggerPrice: Nullable<string | null>;
  positionSizeUsd: Nullable<BigNumber>;
  isLeverageReachMax: boolean;
}): {
  valid: boolean;
  error?: TriggerPriceError;
} => {
  if (
    isNil(triggerPrice) ||
    isEmpty(triggerPrice) ||
    !positionSizeUsd ||
    isNil(marketPrice)
  ) {
    return {
      valid: true,
    };
  }

  const zeroValidation = zeroInput.validator({
    input: new BigNumber(triggerPrice),
  });

  if (zeroValidation.error) {
    return {
      valid: false,
      error: zeroValidation.error,
    };
  }

  if (
    getOpenTradeQty({
      entryPrice: new BigNumber(triggerPrice),
      isLeverageReachMax,
      positionSize: positionSizeUsd,
    }) <= 0n
  ) {
    /**
     * it happens if the trigger price is set too high or low resulting in when position is created,
     * the pair amount (qty) is below 1 unit that it cant be represent with 10 decimal offset
     */

    return {
      valid: false,
      error: 'should_have_non_zero_position_size',
    };
  }

  if (isLong) {
    if (Number(triggerPrice) > marketPrice) {
      return {
        valid: false,
        error: 'should_be_below_market_price',
      };
    }
  } else {
    if (Number(triggerPrice) < marketPrice) {
      return {
        valid: false,
        error: 'should_be_above_market_price',
      };
    }
  }

  return {
    valid: true,
  };
};

export const getTriggerPriceErrorMessage = (
  t: TFunction,
  error?: TriggerPriceError,
) => {
  if (!error) {
    return '';
  }

  switch (error) {
    case 'zero_input':
      return (
        <Text display="inline" mr={1}>
          {t('common.Input should not be zero')}
        </Text>
      );
    case 'should_have_non_zero_position_size':
      return (
        <Text display="inline" mr={1}>
          {t('common.Trigger price is too high')}
        </Text>
      );
    case 'should_be_above_market_price':
      return (
        <Box>
          <Text display="inline" mr={1}>
            {t('common.Trigger price must be higher than market')}
          </Text>
          <Link href={GIT_BOOK_TP_SL_ORDERS} display="inline" target="_blank">
            {t('common.Learn more')}
          </Link>
        </Box>
      );

    case 'should_be_below_market_price':
      return (
        <Box>
          <Text display="inline" mr={1}>
            {t('common.Trigger price must be lower than market')}
          </Text>
          <Link href={GIT_BOOK_TP_SL_ORDERS} display="inline" target="_blank">
            {t('common.Learn more')}
          </Link>
        </Box>
      );
  }
};
